import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Check, FileUp, LogIn, Trash2, Upload } from 'lucide-react'
import axios from 'axios'
import SEO from '../components/SEO'
import './DocumentScreen.css'

const MAX_BYTES = 10 * 1024 * 1024

// Defined outside the component so it is a stable reference.
const syncDialog = (ref, open) => {
  const node = ref.current
  if (!node) return
  if (open && !node.open) node.showModal()
  if (!open && node.open) node.close()
}

function DocumentScreen() {
  const [currentDocument, setCurrentDocument] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [userInfo, setUserInfo] = useState(null)
  const [showProcessing, setShowProcessing] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const processingRef = useRef(null)
  const deleteRef = useRef(null)

  useEffect(() => {
    const userInfoFromStorage = JSON.parse(localStorage.getItem('userInfo'))
    setUserInfo(userInfoFromStorage)
  }, [])

  const fetchCurrentDocument = useCallback(async () => {
    if (!userInfo) return
    try {
      const config = {
        headers: { Authorization: `Bearer ${userInfo.accessToken}` },
      }
      const response = await axios.get(process.env.REACT_APP_GOLANGAPI + '/api/doc', config)
      if (response.data && response.data.filename) {
        setCurrentDocument({
          name: response.data.filename,
          uploadTime: response.data.uploadTime,
          size: response.data.size || 'Unknown',
        })
      } else {
        setCurrentDocument(null)
      }
    } catch (error) {
      console.error('Error fetching current document:', error)
      setCurrentDocument(null)
    }
  }, [userInfo])

  useEffect(() => {
    if (userInfo) fetchCurrentDocument()
  }, [userInfo, fetchCurrentDocument])

  useEffect(() => syncDialog(processingRef, showProcessing), [showProcessing])
  useEffect(() => syncDialog(deleteRef, showDeleteDialog), [showDeleteDialog])

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'Unknown'
    const date = new Date(dateTimeStr)
    return date.toLocaleString()
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const processFile = async (file) => {
    if (!file) return

    if (!file.type.includes('pdf')) {
      setUploadError('Please upload a PDF file only')
      return
    }

    if (file.size > MAX_BYTES) {
      setUploadError('File size exceeds 10MB limit')
      return
    }

    setUploading(true)
    setUploadError('')
    setShowProcessing(true)

    const formData = new FormData()
    formData.append('document', file)
    formData.append('filename', file.name)

    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo.accessToken}`,
        },
      }

      const response = await axios.post(
        process.env.REACT_APP_GOLANGAPI + '/api/doc',
        formData,
        config
      )
      setCurrentDocument({
        name: file.name,
        uploadTime: response.data.uploadTime,
        size: formatFileSize(file.size),
      })
    } catch (error) {
      console.error('Upload error:', error)
      setUploadError(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message ||
              'Error uploading document. Please check your connection and try again.'
      )
    } finally {
      setUploading(false)
      setShowProcessing(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) processFile(files[0])
  }

  const handleDeleteDocument = async () => {
    if (!userInfo || !currentDocument) return

    setDeleting(true)
    try {
      const config = {
        headers: { Authorization: `Bearer ${userInfo.accessToken}` },
      }
      await axios.delete(process.env.REACT_APP_GOLANGAPI + '/api/doc', config)
      setCurrentDocument(null)
      setShowDeleteDialog(false)
    } catch (error) {
      console.error('Delete error:', error)
      setUploadError('Error deleting document. Please try again.')
      setShowDeleteDialog(false)
    } finally {
      setDeleting(false)
    }
  }

  const seo = (
    <SEO
      title="Document Manager - PCRAssist"
      description="Manage your protocol documents for AI-powered analysis."
      canonicalUrl="/documents"
    />
  )

  if (!userInfo) {
    return (
      <>
        {seo}
        <div className="dm">
          <div className="dm__wrap">
            <header className="dm__head">
              <h1 className="dm__title">Document manager</h1>
            </header>
            <div className="dm__gate">
              <p>You need to be signed in to manage your protocol documents.</p>
              <Link className="dm__btn dm__btn--primary" to="/login">
                <LogIn size={16} aria-hidden="true" />
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {seo}
      <div className="dm">
        <div className="dm__wrap">
          <header className="dm__head">
            <h1 className="dm__title">Document manager</h1>
            <p className="dm__lede">
              The protocol document PCRAssist answers your questions against.
            </p>
          </header>

          {/* ---- current document ---------------------------------- */}
          <section className="dm__section" aria-labelledby="dm-current">
            <h2 id="dm-current">Current document</h2>

            {currentDocument ? (
              <>
                <dl className="dm__facts">
                  <div className="dm__fact">
                    <dt>Filename</dt>
                    <dd>{currentDocument.name}</dd>
                  </div>
                  <div className="dm__fact">
                    <dt>Uploaded</dt>
                    <dd>{formatDateTime(currentDocument.uploadTime)}</dd>
                  </div>
                  <div className="dm__fact">
                    <dt>Status</dt>
                    <dd>
                      <span className="dm__chip">
                        <Check size={13} aria-hidden="true" />
                        Processed
                      </span>
                    </dd>
                  </div>
                </dl>

                <div className="dm__actions">
                  <button
                    type="button"
                    className="dm__btn dm__btn--danger"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    Delete document
                  </button>
                </div>
              </>
            ) : (
              <p className="dm__none">
                No document uploaded. Questions will be answered using the default Denver Health
                protocol.
              </p>
            )}
          </section>

          {/* ---- upload -------------------------------------------- */}
          <section className="dm__section" aria-labelledby="dm-upload">
            <h2 id="dm-upload">{currentDocument ? 'Replace document' : 'Upload document'}</h2>

            {/* A label wrapping a real file input: mouse, keyboard and screen
                readers all reach it. Drag handlers ride on the same element. */}
            <label
              className="dm__drop"
              data-drag={isDragOver}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                setIsDragOver(false)
              }}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => processFile(e.target.files[0])}
                disabled={uploading}
              />
              <Upload className="dm__drop-icon" size={22} aria-hidden="true" />
              <span className="dm__drop-title">Drag a PDF here, or choose a file</span>
              <span className="dm__drop-meta">PDF only &middot; up to 10MB</span>
              <span className="dm__faux">
                <FileUp size={16} aria-hidden="true" />
                Choose PDF file
              </span>
            </label>

            {uploadError && (
              <p className="dm__error" role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                <span>{uploadError}</span>
              </p>
            )}

            <div className="dm__tips">
              <span className="dm__tips-label">Tips for best results</span>
              <ul>
                <li>Upload high-quality, searchable PDF documents</li>
                <li>Ensure text is not scanned images but actual text content</li>
                <li>Protocol documents work best when well-organized with clear sections</li>
              </ul>
            </div>
          </section>
        </div>

        {/* ---- processing ------------------------------------------ */}
        <dialog
          ref={processingRef}
          className="dm__dialog"
          onClose={() => setShowProcessing(false)}
          aria-labelledby="dm-processing-title"
        >
          <div className="dm__dialog-head">
            <h2 id="dm-processing-title">Processing document</h2>
          </div>
          <div className="dm__dialog-body">
            <p>
              Analyzing and indexing your document for optimal search performance. This may take up
              to 30 seconds for large files.
            </p>
            <p className="dm__drop-meta">
              You can close this — the upload continues in the background.
            </p>
          </div>
        </dialog>

        {/* ---- delete confirmation --------------------------------- */}
        <dialog
          ref={deleteRef}
          className="dm__dialog"
          onClose={() => setShowDeleteDialog(false)}
          aria-labelledby="dm-delete-title"
        >
          <div className="dm__dialog-head">
            <h2 id="dm-delete-title">Delete this document?</h2>
          </div>
          <div className="dm__dialog-body">
            <p>
              <strong>{currentDocument ? currentDocument.name : ''}</strong> will be removed. This
              cannot be undone.
            </p>
            <p>
              Questions will be answered using the default Denver Health protocol until you upload a
              new document.
            </p>
          </div>
          <div className="dm__dialog-foot">
            <button
              type="button"
              className="dm__btn dm__dialog-foot-spacer"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="dm__btn dm__btn--danger"
              onClick={handleDeleteDocument}
              disabled={deleting}
            >
              <Trash2 size={16} aria-hidden="true" />
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </dialog>
      </div>
    </>
  )
}

export default DocumentScreen
